import { useEffect, useState } from 'react';
import { LinkIcon2, PlusIcon } from '../../../../svgs/svgs';
import AddIntakeLinkModal from './AddIntakeLinkModal/AddIntakeLinkModal';
import IntakeService from '../../../../services/IntakeService';
import { Icon } from '@iconify/react/dist/iconify.js';
import DeleteIntakeLinkModal from './DeleteIntakeLinkModal/DeleteIntakeLinkModal';
import type { Intake, IntakeLink } from '@/types';

interface IntakeLinksProps {
  intake: Intake | null;
  onLinksUpdate?: (links: IntakeLink[]) => void;
}

export default function IntakeLinks({
  intake,
  onLinksUpdate,
}: IntakeLinksProps) {
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState<IntakeLink | null>(null);
  const [intakeLinks, setIntakeLinks] = useState<IntakeLink[]>([]);

  const refetchIntakeLinks = async () => {
    if (!intake?._id) return;
    try {
      const res = await IntakeService.getIntakeLinks(intake._id);
      setIntakeLinks(res);
      onLinksUpdate?.(res);
    } catch (error) {
      console.error('Error fetching intake links:', error);
    }
  };

  useEffect(() => {
    if (intake?._id) {
      refetchIntakeLinks();
    }
  }, [intake?._id]);

  const handleClick = () => {
    setOpenModal(true);
  };

  return (
    <>
      <div className="w-full flex items-center gap-4 justify-between">
        <div className="w-full flex items-center gap-1">
          <div className="w-4">
            <LinkIcon2 />
          </div>
          <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
            Links
          </h3>
        </div>
        <div className="text-navigation-accent-1 flex items-center gap-1">
          <div className="w-4">
            <PlusIcon />
          </div>
          <p
            onClick={handleClick}
            className="text-xs whitespace-nowrap cursor-pointer hover:underline"
          >
            Add new link
          </p>
        </div>
      </div>
      <div
        onClick={handleClick}
        className={`border-0.5 border-gray-3/20 rounded-md bg-modal-input ${intakeLinks.length > 0 ? 'p-3' : 'p-5'} mt-2`}
      >
        {intakeLinks.length === 0 ? (
          <div className="flex items-center gap-2">
            <div className="w-5">
              <PlusIcon />
            </div>
            <p className="text-sm">Click to add link</p>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {intakeLinks.map((link) => (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLink(link);
                  setOpenDeleteModal(true);
                }}
                className="flex items-center justify-between rounded-lg gap-4 max-w-60 p-2 bg-[#141414] hover:bg-black-7"
                key={link._id}
              >
                <p className="truncate text-xs" title={link.intake_link_name}>
                  {link.intake_link_name || link.intake_link_url}
                </p>

                <button className="p-0.5 hover:bg-red-500/10 rounded-full border-0.5 border-text-primary-body hover:border-red-500/10 transition-colors group disabled:opacity-50">
                  <Icon
                    icon={'qlementine-icons:close-16'}
                    className="w-3 h-3 text-text-primary-body group-hover:text-error-2"
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddIntakeLinkModal
        open={openModal}
        close={() => setOpenModal(false)}
        intake={intake}
        refetch={refetchIntakeLinks}
      />
      <DeleteIntakeLinkModal
        open={openDeleteModal}
        close={() => setOpenDeleteModal(false)}
        intake={intake}
        link={selectedLink}
        refetch={refetchIntakeLinks}
      />
    </>
  );
}
